/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, concatMap, debounceTime, exhaustMap, filter, forkJoin, map, of, switchMap, take, throwError} from "rxjs";
import {DealService} from "../../services/deal.service";
import {
    DealActions,
    DealCompanyActions,
    DealDetailsAction,
    DealFundActions,
    DealFundsManagementActions,
    DealFxAnalysisActions,
    DealListActions,
    DealNameActions,
    DealOverviewActions,
    DealReturnSummaryActions,
    DealWeightedCashflowBreakdownActions,
    SellerPositionActions,
} from "../deal.actions";
import {Store} from "@ngrx/store";
import {selectSelectedSeller} from "../seller/seller.selectors";
import {selectDealFunds, selectDealFundsOverview, selectDealOverviewParams, selectSelectedDeal, selectSelectedDealId} from "./deal.selectors";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {FundService} from "../../../fund/services/fund.service";
import {selectSelectedSourceDataset} from "../../../import/store/import.selectors";
import {FundValuationService} from "../../../fund/services/fund-valuation.service";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {summarizeBulkResults, toBulkResult} from "../../../shared/utils/bulk-operation-util";
import {EMPTY_DEAL} from "../../models/deal";
import {describeDiscountToNavChange} from "../../utils/deal-fx-message.util";
import {concatLatestFrom} from "@ngrx/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";


// #############################################################################################################
// DEAL LIST EFFECTS - START
// #############################################################################################################

export const triggerDealSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                DealOverviewActions.changePage,
                DealOverviewActions.changeSort,
                DealOverviewActions.changeSearch,
                DealOverviewActions.changeArchived
            ),
            map(() => DealOverviewActions.search())
        );
    },
    {functional: true}
);

export const searchDeals = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.search),
            concatLatestFrom(() => store$.select(selectDealOverviewParams)),
            switchMap(([, params]) =>
                dealService.searchDeals(params).pipe(
                    map((result) => DealOverviewActions.searchSuccess({result})),
                    catchError((errorResponse: any) => of(DealOverviewActions.searchError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// When a search lands on a page past the end (e.g. after archiving every row on the last page),
// the backend returns empty content with a number past totalPages. Clamp back to the last real page.
export const clampDealOverviewPageWhenOutOfRange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.searchSuccess),
            filter(({result}) => result.content.length === 0 && result.totalElements > 0 && result.number > result.totalPages - 1),
            map(({result}) => DealOverviewActions.changePage({page: result.totalPages - 1, size: result.size}))
        );
    },
    {functional: true}
);

export const archiveDealsFromOverview = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.archiveDeals),
            exhaustMap((action) => {
                if (action.deals.length === 0) {
                    return of(DealOverviewActions.archiveDealsError({errorMsg: "No deals selected"}));
                }
                return forkJoin(
                    action.deals.map((deal) => toBulkResult(dealService.archiveDeal(deal.id, action.archived)))
                ).pipe(
                    map((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? DealOverviewActions.archiveDealsError({errorMsg: firstError ?? "Deals could not be archived."})
                            : DealOverviewActions.archiveDealsSuccess({count, failed, archived: action.archived});
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadDealsAfterArchive = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.archiveDealsSuccess),
            map(() => DealOverviewActions.search())
        );
    },
    {functional: true}
);

export const showArchiveDealsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.archiveDealsSuccess),
            tap((action) => {
                const noun = action.count === 1 ? "deal" : "deals";
                const verb = action.archived ? "archived" : "unarchived";
                const failedSuffix = action.failed > 0 ? ` ${action.failed} failed.` : "";
                snackBar.open(`${action.count} ${noun} ${verb}.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showArchiveDealsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.archiveDealsError),
            tap((action) => {
                snackBar.open("Deals could not be archived. " + action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const deleteDealsFromOverview = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.deleteDeals),
            exhaustMap((action) => {
                if (action.deals.length === 0) {
                    return of(DealOverviewActions.deleteDealsError({errorMsg: "No deals selected"}));
                }
                return forkJoin(
                    action.deals.map((deal) => toBulkResult(dealService.deleteDeal(deal.id)))
                ).pipe(
                    map((results) => {
                        const {count, failed, firstError} = summarizeBulkResults(results);
                        return count === 0
                            ? DealOverviewActions.deleteDealsError({errorMsg: firstError ?? "Deals could not be deleted."})
                            : DealOverviewActions.deleteDealsSuccess({count, failed});
                    })
                );
            })
        );
    },
    {functional: true}
);

export const reloadDealsAfterBulkDelete = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.deleteDealsSuccess),
            map(() => DealOverviewActions.search())
        );
    },
    {functional: true}
);

export const showDeleteDealsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.deleteDealsSuccess),
            tap((action) => {
                const noun = action.count === 1 ? "deal" : "deals";
                const failedSuffix = action.failed > 0 ? ` ${action.failed} failed.` : "";
                snackBar.open(`${action.count} ${noun} deleted.${failedSuffix}`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showDeleteDealsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealOverviewActions.deleteDealsError),
            tap((action) => {
                snackBar.open("Deals could not be deleted. " + action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadDealList = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealListActions.load),
            exhaustMap(() =>
                dealService.getDeals().pipe(
                    map((deals) => DealListActions.loadSuccess({deals})),
                    catchError((errorResponse: any) => of(DealListActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const createDeal = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.create),
            exhaustMap((action) => {
                return dealService.create({
                        ...EMPTY_DEAL,
                        projectName: {text: action.dealName},
                        closingDate: {date: action.closingDate},
                        currencyIso: {code: action.currencyIso}
                    }
                ).pipe(
                    map((deal) => DealActions.createSuccess({dealWithSeller: {deal}})),
                    catchError((errorResponse: any) => of(DealActions.createError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const navigateToDealDetailsPageAfterCreate = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(DealActions.createSuccess),
            tap((action) => router$.navigate(["/deals", action.dealWithSeller.deal.id])));
    },
    {functional: true, dispatch: false}
);

export const copyDeal = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.copy),
            exhaustMap((action) => {
                return dealService.copyDeal(action.dealId, action.closingDate, action.projectName).pipe(
                    map((deal) => DealActions.copySuccess({deal})),
                    catchError((errorResponse: any) => of(DealActions.copyError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const navigateToDealDetailsPageAfterCopy = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(DealActions.copySuccess),
            tap((action) => router$.navigate(["/deals", action.deal.id])));
    },
    {functional: true, dispatch: false}
);

export const closeCopyDialogAfterCopy = createEffect(
    (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
        return actions$.pipe(
            ofType(DealActions.copySuccess),
            tap(() => dialog.closeAll())
        );
    },
    {functional: true, dispatch: false}
);

export const showCopyError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealActions.copyError),
            tap(({errorMsg}) => snackBar.open(errorMsg, "", {duration: 5000}))
        );
    },
    {functional: true, dispatch: false}
);

// #############################################################################################################
// DEAL LIST EFFECTS - END
// #############################################################################################################

export const loadDealData = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.loadDealData),
            exhaustMap((action) => {
                const dealId = action.dealId;
                return dealService.getDealData(dealId).pipe(
                    map((dealData) => DealActions.loadDealDataSuccess({dealData})),
                    catchError((errorResponse: any) => of(DealActions.loadDealDataError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

/* DELETE DEAL */
export const deleteDeal = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.delete),
            exhaustMap(action =>
                dealService.deleteDeal(action.dealId).pipe(
                    map(() => DealActions.deleteSuccess()),
                    catchError((errorResponse: any) => of(DealActions.deleteError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const reloadDealListAndNavigateAfterDelete = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(DealActions.deleteSuccess),
            tap(() => router$.navigate(["/deals"])),
            map(() => DealOverviewActions.search())
        );
    },
    {functional: true}
);

export const archiveDeal = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.archive),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            exhaustMap(([action, dealId]) => {
                if (dealId != null) {
                    return dealService.archiveDeal(dealId, action.archived).pipe(
                        map(dealWithSeller => DealActions.archiveSuccess({dealWithSeller})),
                        catchError((errorResponse: any) => of(DealActions.archiveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(DealActions.archiveError({errorMsg: "Deal could not be found"}));
                }
            })
        );
    },
    {functional: true}
);

export const showArchiveDealSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealActions.archiveSuccess),
            tap((action) => {
                const verb = action.dealWithSeller.deal.isArchived?.value ? "archived" : "unarchived";
                snackBar.open(`Deal successfully ${verb}.`, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showArchiveDealError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealActions.archiveError),
            tap((action) => {
                snackBar.open("Deal could not be archived. " + action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const saveDealName = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealNameActions.save),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            exhaustMap(([action, dealId]) => {
                if (dealId === undefined) return of(DealNameActions.saveError({errorMsg: "Deal could not be found."}));
                return dealService.updateDealName(dealId, action.dealName).pipe(
                    map((dealWithSeller) => DealNameActions.saveSuccess({dealWithSeller})),
                    catchError((errorResponse: any) => of(DealNameActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveSellerOnDeal = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealActions.saveSellerOnDeal),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            exhaustMap(([action, dealId]) => {
                if (dealId === undefined) return of(DealActions.saveSellerOnDealError({errorMsg: "Deal could not be found."}));
                return dealService.updateSellerId(
                    dealId,
                    action.sellerId
                ).pipe(
                    map((dealWithSeller) => DealActions.saveSellerOnDealSuccess({dealWithSeller})),
                    catchError((errorResponse: any) => of(DealActions.saveSellerOnDealError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const loadFundsForDealFundManagement = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundService = inject(FundService)) => {
        return actions$.pipe(
            ofType(DealFundsManagementActions.loadFunds),
            concatLatestFrom(() => store$.select(selectSelectedSourceDataset)),
            exhaustMap(([, dataset]) =>
                fundService.getFunds(dataset, false).pipe(
                    map((funds) => DealFundsManagementActions.loadFundsSuccess({funds})),
                    catchError((errorResponse: any) => of(DealFundsManagementActions.loadFundsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadFundValuationsForDealFundManagement = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), fundValuationService = inject(FundValuationService)) => {
        return actions$.pipe(
            ofType(DealFundsManagementActions.loadFundValuations),
            exhaustMap((action) =>
                fundValuationService.getValuationsForFund(action.fundId).pipe(
                    map((fundValuations) => DealFundsManagementActions.loadFundValuationsSuccess({fundValuations})),
                    catchError((errorResponse: any) => of(DealFundsManagementActions.loadFundValuationsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadDealFunds = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealFundActions.load),
            exhaustMap((action) => {
                return dealService.getDealFunds(action.dealId).pipe(
                    map((dealFund) => DealFundActions.loadSuccess({dealFund})),
                    catchError((errorResponse: any) => of(DealFundActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const loadDealCompanies = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealCompanyActions.load),
            exhaustMap((action) => {
                return dealService.getDealCompanies(action.dealId).pipe(
                    map((dealCompany) => DealCompanyActions.loadSuccess({dealCompany})),
                    catchError((errorResponse: any) => of(DealCompanyActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveDealFunds = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealFundActions.save),
            exhaustMap((action) => {
                return dealService.updateDealFunds(action.dealId, action.sellerPositions, action.bidPriceAdjustment).pipe(
                    map((dealData) => DealFundActions.saveSuccess({dealData})),
                    catchError((errorResponse: any) => of(DealFundActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

// #############################################################################################################
// DEAL FX ANALYSIS / MANAGER EFFECTS (US-1172) - START
// #############################################################################################################

export const loadDealFxAnalysis = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.load),
            switchMap((action) =>
                dealService.getDealFxAnalysis(action.dealId).pipe(
                    map((fxAnalysis) => DealFxAnalysisActions.loadSuccess({fxAnalysis})),
                    catchError((errorResponse: any) => of(DealFxAnalysisActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// Single dedicated effect owning the whole apply sequence: currencies -> fund->deal -> asset->fund.
// It does NOT re-dispatch DealFundActions.save (which carries no correlation id); it calls the services
// directly and emits FX-specific success/error so the reload + snackbar can enumerate per-step outcome.
export const applyFxRates = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.applyRates),
            concatLatestFrom(() => store$.select(selectDealFundsOverview)),
            concatMap(([action, overview]) => {
                const ccyCount = action.fundCurrencies.length + action.assetCurrencies.length;
                const fundCount = action.fundRates.length;
                const assetCount = action.assetRates.length;
                let ccyDone = false;
                let fundDone = false;

                const currencies$ = ccyCount > 0
                    ? dealService.setDealCurrencies(action.dealId, action.fundCurrencies, action.assetCurrencies).pipe(map(() => null))
                    : of(null);

                return currencies$.pipe(
                    tap(() => (ccyDone = true)),
                    concatMap(() => {
                        if (fundCount === 0) return of(null);
                        const ratesById = new Map(action.fundRates.map((r) => [r.sellerPositionId, r]));
                        let matched = 0;
                        const updatedSellerPositions = overview.sellerPositions.map((sp) => {
                            const assignment = sp.id !== undefined ? ratesById.get(sp.id) : undefined;
                            if (assignment) {
                                matched++;
                            }
                            return assignment
                                ? {...sp, fxRateDeal: {...sp.fxRateDeal, fraction: assignment.fraction, source: assignment.source}}
                                : sp;
                        });
                        // Guard against a stale/empty funds snapshot silently writing nothing yet reporting success.
                        if (matched < fundCount) {
                            return throwError(() => new Error(
                                `Deal funds not fully loaded (${matched}/${fundCount} fund rate targets matched). Reopen the deal and retry.`));
                        }
                        // bidPriceAdjustment intentionally omitted: DealFundServiceImpl.updateDealFunds only
                        // writes it when non-null, so undefined leaves the deal's existing adjustment untouched —
                        // the desired behaviour for an FX-only apply (it must not clear the bid price adjustment).
                        return dealService.updateDealFunds(action.dealId, updatedSellerPositions);
                    }),
                    tap(() => (fundDone = true)),
                    concatMap(() => (assetCount === 0 ? of(null) : dealService.updateAssetFxRates(action.dealId, action.assetRates))),
                    map(() => DealFxAnalysisActions.applyRatesSuccess({dealId: action.dealId, fundCount, assetCount, ccyCount})),
                    catchError((errorResponse: any) =>
                        of(DealFxAnalysisActions.applyRatesError({
                            dealId: action.dealId,
                            stepReached: !ccyDone ? "currencies" : !fundDone ? "fund" : "asset",
                            ccyDone,
                            fundDone,
                            assetDone: false,
                            errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)
                        })))
                );
            })
        );
    },
    {functional: true}
);

// After an apply (success OR partial failure) reload the FX analysis and the deal data so the dialog and
// the "Funds (in Deal Currency)" card reflect actual persisted state. loadDealData refreshes the funds
// slice too (its success reducer maps dealData.dealFund), so a separate DealFundActions.load would be a
// duplicate fetch — showFxRecalcComplete keys off loadDealDataSuccess instead.
export const reloadAfterFxApply = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.applyRatesSuccess, DealFxAnalysisActions.applyRatesError),
            switchMap((action) => [
                DealFxAnalysisActions.load({dealId: action.dealId}),
                DealActions.loadDealData({dealId: action.dealId})
            ])
        );
    },
    {functional: true}
);

export const showFxApplySuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.applyRatesSuccess),
            tap(({ccyCount, fundCount, assetCount}) => {
                const parts: string[] = [];
                if (ccyCount) parts.push(`${ccyCount} currenc${ccyCount === 1 ? "y" : "ies"} set`);
                if (fundCount) parts.push(`${fundCount} fund ${fundCount === 1 ? "rate" : "rates"}`);
                if (assetCount) parts.push(`${assetCount} asset ${assetCount === 1 ? "rate" : "rates"}`);
                snackBar.open(`Applied — ${parts.join(" · ")}. Deal totals recalculating…`, "", {duration: 6000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showFxApplyError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.applyRatesError),
            tap(({ccyDone, fundDone, stepReached, errorMsg}) => {
                const parts: string[] = [];
                if (ccyDone) parts.push("Currencies saved ✓");
                if (fundDone) parts.push("Fund rates applied ✓");
                parts.push(`${stepReached === "currencies" ? "Currencies" : stepReached === "fund" ? "Fund rates" : "Asset rates"} failed ✗`);
                snackBar.open(`${parts.join(" · ")} — ${errorMsg}. Reopen Manage FX Rates to retry.`, "", {duration: 10000});
            })
        );
    },
    {functional: true, dispatch: false}
);

// After an FX apply, wait for the reloaded deal data (loadDealData, dispatched by reloadAfterFxApply) and
// confirm in the snackbar how the FX change moved the discount to NAV. The discount is recomputed live on
// read (getDealFund), so it is fresh here; the cash-flow *projection* aggregate recomputes asynchronously
// (AFTER_COMMIT), so the wording says projections are "refreshing" rather than claiming they are done.
// Keyed off loadDealDataSuccess (not DealFundActions.load) and filtered by dealId so a concurrent/unrelated
// load cannot satisfy it; loadDealDataError gives a terminal branch so the snackbar can never hang.
export const showFxRecalcComplete = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealFxAnalysisActions.applyRatesSuccess),
            concatLatestFrom(() => store$.select(selectDealFunds)),
            switchMap(([{dealId}, fundsBefore]) => {
                const before = fundsBefore.totalDiscountToNav?.fraction;
                return actions$.pipe(
                    ofType(DealActions.loadDealDataSuccess, DealActions.loadDealDataError),
                    filter((a) => a.type === DealActions.loadDealDataError.type
                        || (a.type === DealActions.loadDealDataSuccess.type && a.dealData.deal.data?.id === dealId)),
                    take(1),
                    tap((a) => {
                        if (a.type === DealActions.loadDealDataError.type) {
                            snackBar.open("FX rates applied, but reloading the deal failed — reopen the deal to see updated totals.", "", {duration: 8000});
                            return;
                        }
                        const after = a.dealData.dealFund?.data?.totalDiscountToNav?.fraction;
                        const parts = ["FX rates applied"];
                        if (before != null && after != null) {
                            parts.push(describeDiscountToNavChange(
                                DecimalFormatPipe.transformFractionToPercent(before) ?? 0,
                                DecimalFormatPipe.transformFractionToPercent(after) ?? 0));
                        }
                        parts.push("cash flow projections are refreshing");
                        snackBar.open(`${parts.join(" — ")}.`, "", {duration: 8000});
                    })
                );
            })
        );
    },
    {functional: true, dispatch: false}
);

// #############################################################################################################
// DEAL FX ANALYSIS / MANAGER EFFECTS (US-1172) - END
// #############################################################################################################

export const saveDealDetails = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealDetailsAction.save),
            concatLatestFrom(() => store$.select(selectSelectedSeller)),
            exhaustMap(([action, selectedSeller]) => {
                if (action.deal.id) {
                    const selectedDeal = action.deal;
                    return dealService.update(action.deal.id, {
                        ...selectedDeal,
                        ...{
                            secondaryCarry: {
                                ...selectedDeal.secondaryCarry,
                                fraction: DecimalFormatPipe.transformPercentToFraction(action.secondaryCarry ?? undefined)
                            },
                            secondaryHurdle: {
                                ...selectedDeal.secondaryHurdle,
                                fraction: DecimalFormatPipe.transformPercentToFraction(action.secondaryHurdle ?? undefined)
                            },
                            secondaryManagementFee: {
                                ...selectedDeal.secondaryManagementFee,
                                fraction: DecimalFormatPipe.transformPercentToFraction(action.secondaryManagementFee ?? undefined)
                            },
                            currencyIso: {
                                ...selectedDeal.currencyIso,
                                code: action.currencyIso
                            },
                            source: {
                                ...selectedDeal.source,
                                text: action.source ?? undefined
                            },
                            processType: {
                                ...selectedDeal.processType,
                                code: action.processType
                            },
                            dealCaptain: {
                                ...selectedDeal.dealCaptain,
                                text: action.dealCaptain ?? undefined
                            },
                        }
                    }, selectedSeller.id).pipe(
                        map((dealData) => DealDetailsAction.saveSuccess({dealData})),
                        catchError((errorResponse: any) => of(DealDetailsAction.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                    );
                } else {
                    return of(DealDetailsAction.saveError({errorMsg: "Error while loading the deal, please refresh the page"}));
                }
            })
        );
    },
    {functional: true}
);

export const showSaveSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealNameActions.saveSuccess, DealActions.saveSellerOnDealSuccess, SellerPositionActions.saveAllSuccess,
                DealFundActions.saveSuccess, DealDetailsAction.saveSuccess),
            tap(() => {
                snackBar.open("Successfully saved!", "", {
                    duration: 3000
                });
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadDealWeightedCashflowBreakdown = createEffect(
    (actions$ = inject(Actions), dealService = inject(DealService)) => actions$.pipe(
        ofType(DealWeightedCashflowBreakdownActions.load),
        switchMap((action) =>
            dealService.getDealWeightedCashflowBreakdown(action.dealId, action.date, action.cashflowTypes).pipe(
                map((breakdown) => DealWeightedCashflowBreakdownActions.loadSuccess({breakdown})),
                catchError((error: any) => of(DealWeightedCashflowBreakdownActions.loadError({
                    errorMsg: ErrorHandlingUtil.getErrorMessage(error)
                })))
            )
        )
    ),
    {functional: true}
);

// #############################################################################################################
// DEAL RETURN SUMMARY EFFECTS
// #############################################################################################################

export const previewDealReturnSummary = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealReturnSummaryActions.calc),
            debounceTime(300),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            switchMap(([action, dealId]) => {
                if (dealId === undefined) {
                    return of(DealReturnSummaryActions.calcError({errorMsg: "Deal could not be found"}));
                }
                return dealService.previewDealReturnSummary(dealId, {
                    bidPriceAdjustment: action.bidPriceAdjustment,
                    editedField: "BID_PRICE_ADJUSTMENT",
                }).pipe(
                    map((dealReturnSummary) => DealReturnSummaryActions.calcSuccess({dealReturnSummary})),
                    catchError((errorResponse: any) => of(DealReturnSummaryActions.calcError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const saveDealReturnSummary = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealReturnSummaryActions.save),
            concatLatestFrom(() => [store$.select(selectSelectedDealId), store$.select(selectDealFunds), store$.select(selectSelectedDeal)]),
            exhaustMap(([action, dealId, dealFunds, deal]) => {
                if (dealId === undefined) {
                    return of(DealReturnSummaryActions.saveError({errorMsg: "Deal could not be found"}));
                }
                const sellerPositions = dealFunds.dealFundRecords.map(df => df.sellerPosition);
                const goalSeekSettings = deal ? {
                    goalSeekTvpi: deal.goalSeekTvpi?.factor ?? undefined,
                    goalSeekTvpiActive: deal.goalSeekTvpiActive?.value ?? false,
                    goalSeekIrr: deal.goalSeekIrr?.fraction ?? undefined,
                    goalSeekIrrActive: deal.goalSeekIrrActive?.value ?? false,
                    goalSeekDiscount: deal.goalSeekDiscount?.fraction ?? undefined,
                    goalSeekDiscountActive: deal.goalSeekDiscountActive?.value ?? false,
                } : undefined;
                return dealService.updateDealFunds(dealId, sellerPositions, action.bidPriceAdjustment, goalSeekSettings).pipe(
                    map((dealData) => DealReturnSummaryActions.saveSuccess({dealData})),
                    catchError((errorResponse: any) => of(DealReturnSummaryActions.saveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const performDealGoalSeek = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), dealService = inject(DealService)) => {
        return actions$.pipe(
            ofType(DealReturnSummaryActions.goalSeek),
            concatLatestFrom(() => store$.select(selectSelectedDealId)),
            exhaustMap(([action, dealId]) => {
                if (dealId === undefined) {
                    return of(DealReturnSummaryActions.goalSeekError({errorMsg: "Deal could not be found"}));
                }
                return dealService.goalSeekDeal(dealId, {
                    targetTvpi: action.targetTvpi,
                    targetTvpiActive: action.targetTvpiActive,
                    targetIrr: action.targetIrr,
                    targetIrrActive: action.targetIrrActive,
                    targetDiscount: action.targetDiscount,
                    targetDiscountActive: action.targetDiscountActive,
                }).pipe(
                    map((dealReturnSummary) => DealReturnSummaryActions.goalSeekComplete({dealReturnSummary})),
                    catchError((errorResponse: any) => of(DealReturnSummaryActions.goalSeekError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const goalSeekCompleteInfo = createEffect(
    (actions$ = inject(Actions), dialog = inject(MatDialog)) => {
        return actions$.pipe(
            ofType(DealReturnSummaryActions.goalSeekComplete),
            tap(() => dialog.closeAll())
        );
    },
    {functional: true, dispatch: false}
);

export const goalSeekErrorInfo = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(DealReturnSummaryActions.goalSeekError),
            tap(({errorMsg}) => snackBar.open(errorMsg, "", {duration: 5000}))
        );
    },
    {functional: true, dispatch: false}
);

